import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { formatPercentOneDecimal } from '@bingads-webui-campaign/recommendation-core';
import { HighchartReactWrapper } from '@bingads-webui-react/highchart-react-wrapper';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { StyledMenuItemTag } from '@bingads-webui-react/menu-item-tag';
import { TooltipHost, DirectionalHint, TooltipDelay } from '@fluentui/react';
import { StyledPopupSummary } from './popup-summary';
import { getOverallScoreStyle } from './style/overall-score-style';

class RecOptimizationOverallScore extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    overallOptimizationScoreBar: PropTypes.shape({
      Gained: PropTypes.number.isRequired,
      Dismissed: PropTypes.number.isRequired,
      Available: PropTypes.number.isRequired,
    }).isRequired,
    i18n: PropTypes.shape({
      getString: PropTypes.func,
    }).isRequired,
    renderAutoApplyButton: PropTypes.func.isRequired,
    renderDownloadButton: PropTypes.func.isRequired,
    renderHistoryButton: PropTypes.func.isRequired,
    scope: PropTypes.shape({
      values: PropTypes.shape({
        campaignId: PropTypes.string,
      }).isRequired,
    }).isRequired,
    isAsyncApplyEnabled: PropTypes.bool.isRequired,
    isAutoApplyRecommendationEnabled: PropTypes.bool.isRequired,
    showDownloadButton: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super(props);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.state = {
      isHovering: false,
    };
  }

  getChartOptions = () => {
    const { overallOptimizationScoreBar } = this.props;
    return {
      chart: {
        width: 400,
        height: 18,
        margin: [0, 0, 0, 0],
        type: 'bar',
      },
      title: {
        text: null,
      },
      xAxis: {
        visible: false,
      },
      yAxis: {
        visible: false,
        tickPositions: [-0.2, 100.2],
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
        formatter: null,
      },
      plotOptions: {
        bar: {
          animation: false,
          pointWidth: 16,
          borderColor: '#0078D4',
        },
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: null,
            },
          },
          stacking: 'normal',
          states: {
            hover: {
              enabled: false,
            },
            inactive: {
              enabled: false,
            },
          },
        },
      },
      series: [{
        data: [overallOptimizationScoreBar.Available * 100],
        color: '#FFFFFF',
      }, {
        data: [overallOptimizationScoreBar.Dismissed * 100],
        color: '#ABDBFF',
      }, {
        data: [overallOptimizationScoreBar.Gained * 100],
        color: '#0078D4',
      },
      ],
    };
  };

  handleMouseEnter() {
    this.timerForPopupSummary = setTimeout(() => {
      this.setState({ isHovering: true });
    }, 200);
  }

  handleMouseLeave() {
    clearTimeout(this.timerForPopupSummary);
    this.setState({ isHovering: false });
  }

  renderOverallScore = () => {
    const { classes, i18n, overallOptimizationScoreBar } = this.props;
    const { Gained, Dismissed } = overallOptimizationScoreBar;

    return (
      <div
        className={`${classes.scoreValue} overall-score-value`}
        id={Gained + Dismissed === 0 ? 'zero-overall-score' : ''}
      >
        {formatPercentOneDecimal(Gained + Dismissed, i18n)}
      </div>
    );
  };

  render() {
    const {
      classes,
      i18n,
      renderAutoApplyButton,
      renderDownloadButton,
      renderHistoryButton,
      overallOptimizationScoreBar,
      scope,
      isAsyncApplyEnabled,
      isAutoApplyRecommendationEnabled,
      showDownloadButton,
    } = this.props;
    const { Gained, Dismissed } = overallOptimizationScoreBar;
    const overallScore = Gained + Dismissed;

    const calloutContent = i18n.getString(_TL_('The low Optimization Score doesn\'t mean you are not performing well, but represents you have much room to optimize. Applying recommendations will help you improve your Optimization Score and performance.'));
    const hostStyles = {
      root: {
        display: 'inline-block',
      },
    };
    const calloutProps = { gapSpace: 7 };
    const tooltipProps = {
      maxWidth: '231px',
      styles: {
        root: {
          padding: '8px 10px',
        },
      },
      delay: TooltipDelay.zero,
    };

    return (
      <div id="rec-overall-score-container" className={classes.recOverallScoreContainer}>
        <div htmlFor="score" className={classes.scoreLabel}>
          {i18n.getString(_TL_('Your optimization score'))}
        </div>
        <div>
          <HelpPopup
            topic={!_.isUndefined(scope.values.campaignId) ? 'Pop_BA_Recommendations_OptScoreCamp' : 'Pop_BA_Recommendations_OptScoreAcct'}
          />
          {!isAutoApplyRecommendationEnabled &&
            <div className={classes.betaTagContainer}>
              <StyledMenuItemTag tagContent={_TL_('BETA')} tagClass="blueTag" />
            </div>
          }
        </div>
        {overallScore === 0 ?
          <TooltipHost
            content={calloutContent}
            directionalHint={DirectionalHint.bottomCenter}
            styles={hostStyles}
            calloutProps={calloutProps}
            tooltipProps={tooltipProps}
          >
            {this.renderOverallScore()}
          </TooltipHost> : this.renderOverallScore()
        }
        <div
          id="score-bar-chart-container"
          className={classes.scoreBarChartContainer}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
        >
          <HighchartReactWrapper options={this.getChartOptions()} />
          {this.state.isHovering && overallScore > 0 &&
            <StyledPopupSummary
              overallOptimizationScoreBar={overallOptimizationScoreBar}
              i18n={i18n}
              scope={scope}
            />}
        </div>
        {isAutoApplyRecommendationEnabled &&
          <div className={`auto-apply-container ${showDownloadButton ? 'with-download-button' : ''}`}>
            {renderAutoApplyButton()}
          </div>
        }
        {showDownloadButton &&
          <div className={`download-button-container ${isAsyncApplyEnabled ? 'with-history-button' : ''}`}>
            {renderDownloadButton(true)}
          </div>
        }
        {isAsyncApplyEnabled && renderHistoryButton()}
      </div>
    );
  }
}

export const StyledRecOptimizationOverallScore =
  withDefaultStyles((RecOptimizationOverallScore), getOverallScoreStyle);
